import { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
// Import Types
import type { AwardById } from 'types';
// Import UI components
import { Modal, Button } from '@geneui/components';
// Import Components
import BonusInfoModalBody from 'components/BonusModal/BonusInfoModalBody';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Services
import { BonusHttpService } from 'services/http';
// Import Constants
import { l } from 'constants/common';

const AwardIdModal = ({ awardId, isVisible, handleClose }) => {
    const { t } = useTranslation();

    const { doGetRequest } = useRequest();

    const { getBonusById } = useCancelableRequests({
        getBonusById: BonusHttpService.getBonusById,
    });
    const [bonusInfo, setBonusInfo] = useState<Nullable<AwardById>>(null);
    const [isLoadingBonus, setIsLoadingBonus] = useState(false);

    const getBonusInfo = () => {
        setIsLoadingBonus(true);
        doGetRequest(getBonusById.request, {
            queryString: { bonusId: awardId },
            successCallback: (data) => {
                setBonusInfo(data);
            },
        }).then(() => {
            setIsLoadingBonus(false);
        });
    };

    useEffect(() => {
        getBonusInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [awardId]);

    const cleanUp = () => {
        return () => {
            setBonusInfo(null);
            setIsLoadingBonus(false);
            getBonusById.cancel('AwardInfoModal:getBonusById');
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, [awardId]);

    return (
        <Modal
            withPortal={true}
            closeOnClickOutside={true}
            background="dark-background"
            position="center"
            visible={isVisible}
            closable={false}
            onCancel={handleClose}
            className="crm-object-list-modal"
            title={
                <div className="crm-object-list-modal-head">
                    <span className="crm-object-list-modal-h-text">{t(l.BonusInfo)}</span>
                    <div className="crm-object-list-modal-h-icons"></div>
                </div>
            }
            footer={
                <Button color="primary" onClick={handleClose}>
                    {t(l.Close)}
                </Button>
            }
        >
            <BonusInfoModalBody
                id={bonusInfo?.Id}
                wallet={bonusInfo?.BalanceType}
                title={bonusInfo?.Name}
                description={bonusInfo?.Description}
                startDate={bonusInfo?.StartDate}
                endDate={bonusInfo?.EndDate}
                type={bonusInfo?.Type}
                bonusDetails={bonusInfo?.BonusDetails}
                isLoading={isLoadingBonus}
            />
        </Modal>
    );
};
AwardIdModal.propTypes = {
    awardId: PropTypes.number.isRequired,
    isVisible: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
};

export default memo(AwardIdModal);
